import { useCallback, useEffect, useState } from "react"
import { GetApp } from "../../App"
import { Block, BlockRow, BlockField, BlockFieldInputs } from "../../Components/Block"
import { Icon, Icons } from "../../Components/Icon"
import { logger } from "../../App/Logger"
import { GetShipContext } from "../../App/Context"
import { Table } from "../../Components/Table"
import { useConnectedCompanions, useDataStore, type CompanionConnectionsState } from "../../Data"
import { ShipApi } from "../../Types"
import type { IConnectedCompanion } from "../../Types/Generated/LogbookApp/ShipApi"
import { ConfirmDialog } from "../../Components/ConfirmDialog"

interface CompanionsViewProps {
  socketAddress: string;
  connections?: CompanionConnectionsState;
  onDisconnect?: (companionId: string) => Promise<void>;
}

export function CompanionsTab({ socketAddress, shipId }: { socketAddress: string; shipId: number }) {
  const store = useDataStore();
  const { apiUser } = GetShipContext();
  const [revision, setRevision] = useState(0);
  const available = new ShipApi(store.api()).ships.companions({ params: { shipId } }).available(apiUser);
  const canDisconnect = new ShipApi(store.api()).ships.disconnectCompanion({ params: { shipId, companionId: "" } }).available(apiUser);
  const connections = useConnectedCompanions(store, shipId, available, revision);
  const disconnect = useCallback(async (companionId: string) => {
    await new ShipApi(store.api()).ships.disconnectCompanion({ params: { shipId, companionId } }).call();
    setRevision((value) => value + 1);
  }, [store, shipId]);
  return <CompanionsView key={shipId} socketAddress={socketAddress} connections={connections} onDisconnect={canDisconnect ? disconnect : undefined} />;
}

export function CompanionsView({ socketAddress, connections, onDisconnect }: CompanionsViewProps) {
  const app = GetApp()!;
  const t = app.getT("ShipSettingsView");
  const [selected, setSelected] = useState<IConnectedCompanion>();
  useEffect(() => {
    if (!onDisconnect) setSelected(undefined);
  }, [onDisconnect]);

  return (
    <>
      <Block className="tab-content">
        <BlockRow>
          <BlockField className="block_field_full">
            <p>{t("socket_text")}</p>
          </BlockField>
        </BlockRow>
        <BlockRow>
          <BlockField label={t("socket")} className="block_field_small_16">
            <BlockFieldInputs>
              <input value={socketAddress} readOnly={true} />
              <button type="button" className="button enter" onClick={() => {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(socketAddress).catch(console.error);
                } else {
                  // Fallback for older browsers or non-secure contexts
                  const textarea = document.createElement("textarea");
                  textarea.value = socketAddress;
                  textarea.style.position = "fixed";
                  textarea.style.opacity = "0";
                  document.body.appendChild(textarea);
                  textarea.select();
                  try {
                    document.execCommand("copy");
                  } catch (err) {
                    logger.error("Failed to copy:", err);
                  }
                  document.body.removeChild(textarea);
                }
              }}
              >
                <Icon icon={Icons.Copy} />
              </button>
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>
        {connections && (
          <BlockRow>
            <BlockField label={t("connected_companions")} className="block_field_full CompanionsView-connections">
              {connections.status === "loading" && <p role="status">{t("companions_loading")}</p>}
              {connections.status === "error" && <p role="alert">{t("companions_error")}</p>}
              {connections.status === "loaded" && (
                connections.companions.length === 0 ? <p>{t("companions_empty")}</p> : (
                  <Table
                    columnWidths={["minmax(0, 1fr)", "minmax(0, 1fr)", ...(onDisconnect ? ["calc(44px + 2em + 1px)"] : [])]}
                    header={[t("companion_user"), t("companion_host"), ...(onDisconnect ? [""] : [])]}
                    data={connections.companions.map((companion) => ({
                      data: [companion.userName, companion.host],
                      actions: onDisconnect ? [
                        <button
                          key="disconnect"
                          type="button"
                          className="button CompanionsView-disconnect"
                          aria-label={t("disconnect_companion_label", { user: companion.userName, host: companion.host })}
                          title={t("disconnect_companion_label", { user: companion.userName, host: companion.host })}
                          onClick={() => setSelected(companion)}
                        >
                          <span aria-hidden="true"><Icon icon={Icons.Close} /></span>
                        </button>,
                      ] : undefined,
                    }))}
                  />
                )
              )}
            </BlockField>
          </BlockRow>
        )}
      </Block>
      {selected && onDisconnect && (
        <ConfirmDialog
          awaitConfirm={true}
          style="warning CompanionsView-confirm"
          title={t("disconnect_companion_title")}
          message={(
            <BlockField>
              <p><strong>{selected.userName}</strong><br /><span>{selected.host}</span></p>
              <p>{t("disconnect_companion_text")}</p>
            </BlockField>
          )}
          cancelLabel={t("cancel")}
          confirmLabel={t("disconnect_companion")}
          errorMessage={t("disconnect_companion_error")}
          onCancel={() => setSelected(undefined)}
          onConfirm={async () => {
            await onDisconnect(selected.id);
            setSelected(undefined);
          }}
        />
      )}
    </>
  );
}
